/**
 * GeoGebra Bridge —— 见 SPEC.md §4.2 / §4A.3
 *
 * 把 schema 中的 op 翻译为对 GGBAppletApi 的真实调用，并返回每条命令的执行结果。
 * 失败的命令不会中断后续执行，统一返回结果列表交给上层。
 *
 * Phase 1 增强 (2026-08):
 *   - style op 补全 pointSize/pointStyle 原生 API
 *   - 批量执行 setRepaintingActive 包裹（性能优化）
 *   - applyCanvasConfig 画布配置（domain 切换时调用）
 */
import type { Command } from "./schema";
import { shouldBatch, markRepaintBusy, REPAINT_GRACE_MS } from "./repaintGate";
import { validateGGBCommand } from "./commandValidate";
import type { GGBAppletApi } from "../types/ggb";
import { PHYSICS_CONSTANTS, formatGGBNumber } from "./physics";

/** 全局自增临时对象 id：避免 vector/forceDiagram 的辅助矢量名跨命令复用导致动态污染 */
let _tmpSeq = 0;
function genTmpId(prefix: string): string {
  return `${prefix}${_tmpSeq++}`;
}

/** 重置临时对象计数器——画布清空 / applet 重建时调用，避免长会话下临时名无限膨胀 */
export function resetTmpIds(): void {
  _tmpSeq = 0;
}

// ──── scripting 命令识别 ────

/**
 * GGB 的 evalCommand 只对「产生了输出对象」的命令返回 true；
 * Set×、Show×、ZoomIn、StartAnimation 等 scripting 命令**即使执行成功也返回 false**
 * （实测 5.4.927/5.4.929 一致：SetColor 后 getColor 已变更，返回值仍为 false）。
 * 对这类命令不能以返回值判定失败——否则每个 Set 前缀命令都会触发无谓的修复回路/分层重试。
 * 语法/值域错误已由 commandValidate 静态预检在进引擎前拦截。
 *
 * ⚠ Delete 不在豁免内：它成功/失败的返回值同样是 false（不可区分），
 *   但删除成败可以用 api.exists 前后对比验证——见 eval case 的 Delete 特判。
 */
const SCRIPTING_CMD_RE = /^(?:Set|Show)[A-Za-z]|^(?:ZoomIn|ZoomOut|CenterView|Pan|StartAnimation|StopAnimation|SetActiveView|SetSeed|SelectObjects|RunClickScript|RunUpdateScript)\b/;

export function isScriptingCommand(cmd: string): boolean {
  // 剥掉 "obj = " 赋值前缀（scripting 命令不会出现在赋值右侧，此处仅防御性处理）
  const m = /^(?:\w+\s*=\s*)?([A-Za-z_]\w*)/.exec(cmd.trim());
  return !!m && SCRIPTING_CMD_RE.test(m[1]);
}

export interface ExecResult {
  ok: boolean;
  command: Command;
  /** 该 op 实际执行的最底层 GGB 命令（供错误回喂 AI） */
  expanded: string[];
  error?: string;
}

// ──── 视窗宽高比校正 ────

/**
 * 视窗适配：把 AI 请求的视窗范围调整为与画布实际宽高比一致。
 *
 * GeoGebra 的 setCoordSystem 将 xmin..xmax 线性映射到画布像素宽、ymin..ymax 映射到高；
 * 若 AI 给出的范围比例与画布宽高比不符（画布通常宽 > 高，AI 却常给近方形范围），
 * 圆会变椭圆、角度与比例失真。
 * 本函数以 AI 范围中心为锚点，保持中心不变，向外扩展其中一维，使
 *   (xmax-xmin) : (ymax-ymin) == 画布像素宽 : 画布像素高，
 * 且结果范围【包含】AI 请求范围（只扩不缩，不裁剪内容）。
 *
 * @returns 调整后的四元组；无法获取画布尺寸（mock/测试环境）时原样返回。
 */
export function fitViewToAspect(
  api: GGBAppletApi,
  xmin: number, xmax: number, ymin: number, ymax: number
): { xmin: number; xmax: number; ymin: number; ymax: number } {
  const size = getViewSizePx(api);
  if (!size) {
    console.warn("[AiGGB:DIAG] fitViewToAspect: 无法获取绘图区尺寸，视窗原样应用");
    return { xmin, xmax, ymin, ymax };
  }

  const xRange = xmax - xmin;
  const yRange = ymax - ymin;
  if (!(xRange > 0) || !(yRange > 0)) return { xmin, xmax, ymin, ymax };

  const aspect = size.w / size.h; // 画布像素宽高比
  const xMid = (xmin + xmax) / 2;
  const yMid = (ymin + ymax) / 2;

  // 保持中心：取「xRange」与「yRange*aspect」中较大者作最终 x 范围，y 按 aspect 补足
  const finalXRange = Math.max(xRange, yRange * aspect);
  const finalYRange = finalXRange / aspect;

  const fitted = {
    xmin: xMid - finalXRange / 2,
    xmax: xMid + finalXRange / 2,
    ymin: yMid - finalYRange / 2,
    ymax: yMid + finalYRange / 2,
  };

  console.log(
    `[AiGGB:DIAG] fitViewToAspect: canvas=${size.w.toFixed(0)}×${size.h.toFixed(0)} aspect=${aspect.toFixed(3)} | ` +
    `请求 [${xmin},${xmax}]×[${ymin},${ymax}] → 应用 [${fitted.xmin.toFixed(2)},${fitted.xmax.toFixed(2)}]×[${fitted.ymin.toFixed(2)},${fitted.ymax.toFixed(2)}]`
  );
  return fitted;
}

/**
 * 获取画布当前绘图区像素尺寸（宽/高），用于视窗宽高比校正。
 *
 * 测量优先级：
 *   1. #ggb-container 内最大的 <canvas> —— 真正的绘图区（GGB 图形区由 canvas 承载，
 *      canvas 宽高比 == setCoordSystem 实际映射区；容器 rect 含轴标签边距，比例有偏差）
 *   2. 容器 rect（canvas 尚未布局 / 隐藏时回退）
 *   3. GGB 原生 getWidth/getHeight（可能返回注入时的初始尺寸，resize 后过时——最不可靠）
 */
function getViewSizePx(api: GGBAppletApi): { w: number; h: number } | null {
  if (typeof document !== "undefined") {
    const container = document.getElementById("ggb-container");
    if (container) {
      // ★ 首选：绘图 canvas（取面积最大者，排除小 UI canvas）
      let best: { w: number; h: number } | null = null;
      for (const canvas of container.querySelectorAll("canvas")) {
        const r = canvas.getBoundingClientRect();
        if (r.width > 0 && r.height > 0 && (!best || r.width * r.height > best.w * best.h)) {
          best = { w: r.width, h: r.height };
        }
      }
      if (best) return best;
      // 回退：容器 rect
      const rect = container.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) return { w: rect.width, h: rect.height };
    }
  }
  const anyApi = api as unknown as { getWidth?: () => number; getHeight?: () => number };
  if (typeof anyApi.getWidth === "function" && typeof anyApi.getHeight === "function") {
    const w = anyApi.getWidth();
    const h = anyApi.getHeight();
    if (w > 0 && h > 0) return { w, h };
  }
  return null;
}

/** 把所有命令逐条执行，失败不停。批量操作期间暂停重绘以提升性能。 */
export function executeCommands(api: GGBAppletApi, commands: Command[], appMode?: "2d" | "3d"): ExecResult[] {
  // ★ 批量执行：暂停重绘 → 逐条执行 → 恢复重绘（一次性渲染，避免逐条重绘闪烁）
  //    历史：曾因旧 GGB 5.4.920 的 DockGlassPane 崩溃，对 3D 禁用 batch；
  //    5.4.927.1 起 2D/3D 统一启用（禁用 batch 会导致代数区 canvasDef 逐条重建闪烁）。
  //    3D 阈值/开关见 repaintGate.shouldBatch（3D 整屏重绘代价高，且可由用户关闭）。
  const useBatch = shouldBatch(commands.length, appMode);
  // DIAGNOSTIC: 记录 batch 前后的 canvas 数量（仅浏览器）
  const inBrowser = typeof document !== "undefined";
  const canvasBefore = inBrowser
    ? (document.getElementById("ggb-container")?.querySelectorAll("canvas")?.length ?? -1)
    : -1;
  console.log("[AiGGB:DIAG] executeCommands: 开始执行", commands.length, "条命令, batch=", useBatch,
    canvasBefore >= 0 ? `canvas=${canvasBefore}` : "");
  if (useBatch) {
    console.log("[AiGGB:DIAG] setRepaintingActive(false) — 暂停重绘");
    api.setRepaintingActive(false);
  }
  try {
    // ★ 方案 A（静态排序）+ 方案 C（分层重试）：
    //    GGB 严格按依赖顺序执行（先点、后线、再面）。模型生成的命令可能乱序，
    //    仅靠 map 按数组顺序执行会导致依赖对象未就绪而失败，或 GGB 静默创建自由点污染。
    //    - orderCommands：按 op 优先级重排（constants→slider→eval→vector→animate→style）
    //    - 分层重试：第一轮执行全部，失败的进下一轮（此时前面成功的已建好依赖）
    const ordered = orderCommands(commands);
    const results = new Map<number, ExecResult>();

    let pending = ordered;
    for (let pass = 0; pass < MAX_EXEC_PASSES && pending.length > 0; pass++) {
      const retry: Array<{ cmd: Command; idx: number }> = [];
      for (const { cmd, idx } of pending) {
        const r = executeOne(api, cmd, appMode);
        results.set(idx, r);
        if (!r.ok) {
          console.warn(`[AiGGB:DIAG] executeCommands [${idx}] pass${pass + 1}: ❌ ${cmd.op} 执行失败 —`, r.error);
          // 失败且可重试（非幂等 op 不做多次副作用）→ 下轮重试
          if (pass < MAX_EXEC_PASSES - 1 && !NON_IDEMPOTENT_OPS.has(cmd.op)) {
            retry.push({ cmd, idx });
          }
        }
      }
      pending = retry;
    }

    // ★ 对齐原始 commands 数组顺序返回（constructionLog 回滚重放需按模型生成顺序）
    return commands.map((_, i) => results.get(i) as ExecResult);
  } finally {
    if (useBatch) {
      console.log("[AiGGB:DIAG] setRepaintingActive(true) — 恢复重绘 (可能触发大量 GPU 渲染)");
      api.setRepaintingActive(true);
      // ★ 恢复重绘后进入静默期：GGB 会做整屏重绘 + 内部布局重组（3D 下可能有几帧
      //    canvas 空白），期间禁止心跳判定"画布消失"，避免误触硬重建造成二次闪烁
      markRepaintBusy();
      // ★ DIAGNOSTIC: setRepaintingActive(true) 是已知的 canvas 消失触发点
      const canvasAfter = inBrowser
        ? (document.getElementById("ggb-container")?.querySelectorAll("canvas")?.length ?? -1)
        : -1;
      if (canvasAfter === 0 && canvasBefore > 0) {
        console.error(`[AiGGB:DIAG] ⚠⚠⚠ setRepaintingActive(true) 后 canvas 从 ${canvasBefore} → 0！等待 GGB 自建（心跳已挂起 ${REPAINT_GRACE_MS}ms）`);
      } else if (canvasBefore >= 0) {
        console.log(`[AiGGB:DIAG] setRepaintingActive(true) 后 canvas=${canvasAfter} (之前=${canvasBefore})`);
      }
    }
  }
}

/** 分层重试的最大轮数（第 1 轮 + 2 次重试），覆盖 eval 内部依赖乱序 */
const MAX_EXEC_PASSES = 3;

/** op 静态执行优先级：数值越小越先执行。覆盖 GGB 的依赖顺序（先点、后线、再面）。 */
const OP_PRIORITY: Record<string, number> = {
  constants: 0,      // 物理常量先注入
  slider: 1,         // 滑块
  eval: 2,           // 点/线/面（内部乱序靠分层重试解决）
  vector: 3,         // 引用已存在的点
  forceDiagram: 3,
  animate: 4,        // 引用滑块/点
  trace: 4,
  physicsTrace: 4,
  style: 5,          // 引用已创建对象
  caption: 5,
  view: 6,           // 视窗最后
  unitAxes: 6,
  delete: 7,         // 删除放最后（不挡构造）
  reset: 7,
};

/** 非幂等 op：分层重试时跳过（重复执行有副作用） */
const NON_IDEMPOTENT_OPS = new Set(["delete", "reset"]);

/**
 * 按 op 依赖优先级重排命令（方案 A：跨 op 静态排序）。
 * 仅改变执行次序；调用方负责把返回结果映射回原始顺序。
 */
export function orderCommands(commands: Command[]): Array<{ cmd: Command; idx: number }> {
  return commands
    .map((cmd, idx) => ({ cmd, idx }))
    .sort((a, b) => (OP_PRIORITY[a.cmd.op] ?? 9) - (OP_PRIORITY[b.cmd.op] ?? 9));
}

/** 执行一条命令；eval 类命令先做静态语法预检（详见 commandValidate.ts），3D 模式附加禁令检查 */
function executeOne(api: GGBAppletApi, cmd: Command, appMode?: "2d" | "3d"): ExecResult {
  const expanded: string[] = [];
  try {
    if (cmd.op === "eval") {
      // ★ 静态语法预检：把「Sequence 执行失败」这类无语义的引擎报错，
      //   换成「具体错在哪 + 正确形态」的诊断，供修复回路精准自愈
      const rawCmd = (cmd as { cmd: string }).cmd;
      const check = validateGGBCommand(rawCmd, appMode);
      if (!check.ok) {
        expanded.push(rawCmd);
        console.warn("[AiGGB:DIAG] 静态校验拦截 eval 命令：", check.message);
        return { ok: false, command: cmd, expanded, error: check.message };
      }
    }
    switch (cmd.op) {
      case "eval": {
        expanded.push(cmd.cmd);
        // ★ Delete 特判：成功/失败的返回值都是 false（scripting 命令），返回值不可用；
        //   以「执行前存在 && 执行后消失」为成功判据，两种失败分别给出可自愈诊断
        const delTarget = /^(?:\w+\s*=\s*)?Delete\s*\(\s*([^)]+?)\s*\)\s*;?\s*$/.exec(cmd.cmd.trim())?.[1];
        if (delTarget) {
          const existed = api.exists(delTarget);
          api.evalCommand(cmd.cmd);
          const gone = !api.exists(delTarget);
          if (existed && gone) return { ok: true, command: cmd, expanded };
          return {
            ok: false,
            command: cmd,
            expanded,
            error: existed
              ? `对象 ${delTarget} 删除失败（执行后仍存在）`
              : `对象 ${delTarget} 不存在，无法删除；请检查对象名（可用 list_objects 确认）`
          };
        }
        const ok = api.evalCommand(cmd.cmd);
        // ★ scripting 命令成功也返回 false（见 isScriptingCommand 注释），不以此判定失败
        if (!ok && isScriptingCommand(cmd.cmd)) {
          return { ok: true, command: cmd, expanded };
        }
        return { ok, command: cmd, expanded, error: ok ? undefined : "GGB evalCommand 返回 false" };
      }

      case "slider": {
        // GGB Slider 语法： name = Slider(min, max, step, speed, width, isAngle, horizontal, animating, random)
        const sliderCmd = `${cmd.name} = Slider(${cmd.min}, ${cmd.max}, ${cmd.step}, 1, 150, false, true, false, false)`;
        expanded.push(sliderCmd);
        const ok = api.evalCommand(sliderCmd);
        if (!ok) return { ok, command: cmd, expanded, error: "滑块创建失败" };
        // 初值
        api.evalCommand(`SetValue(${cmd.name}, ${cmd.value})`);
        // 标注
        const captionText = cmd.label
          ? cmd.unit
            ? `${cmd.label} = %v ${cmd.unit}`
            : `${cmd.label} = %v`
          : cmd.unit
          ? `${cmd.name} = %v ${cmd.unit}`
          : "";
        if (captionText) {
          api.setCaption(cmd.name, captionText);
          api.setLabelStyle(cmd.name, 3); // NAME_VALUE_CAPTION → 3 (caption)
        }
        return { ok: true, command: cmd, expanded };
      }

      case "animate": {
        // 目标存在性预检：动画目标必须是已存在对象（通常是滑块 / 点）
        if (!api.exists(cmd.target)) {
          return {
            ok: false,
            command: cmd,
            expanded: [],
            error: `动画目标 ${cmd.target} 不存在；请先通过 slider 或 eval 创建该对象，再启动动画`
          };
        }
        if (cmd.speed !== undefined) {
          api.setAnimationSpeed(cmd.target, cmd.speed);
        }
        if (cmd.repeat) {
          // GGB 动画类型：0 oscillating, 1 increasing, 2 decreasing, 3 increasing once
          // ★ 无原生 API，必须走 evalCommand；但 SetAnimationType 在白名单中，合法
          const map = { oscillating: 0, increasing: 1, once: 3 } as const;
          api.evalCommand(`SetAnimationType(${cmd.target}, ${map[cmd.repeat]})`);
        }
        api.setAnimating(cmd.target, cmd.on);
        if (cmd.on) api.startAnimation();
        else api.stopAnimation();
        return { ok: true, command: cmd, expanded };
      }

      case "trace": {
        // 目标存在性预检：轨迹目标必须是已存在对象
        if (!api.exists(cmd.target)) {
          return {
            ok: false,
            command: cmd,
            expanded: [],
            error: `轨迹目标 ${cmd.target} 不存在；请先通过 eval 创建该对象，再开启轨迹`
          };
        }
        api.setTrace(cmd.target, cmd.on);
        return { ok: true, command: cmd, expanded };
      }

      case "style": {
        // ★ 目标存在性预检：原生 setter（setColor 等）对不存在的对象静默 no-op，
        //    若不拦截则 style 恒 ok:true，修复回路/满足度审查完全看不到失败。
        //    （与 animate/trace/delete 的预检保持一致）
        if (!api.exists(cmd.target)) {
          return {
            ok: false,
            command: cmd,
            expanded: [],
            error: `对象 ${cmd.target} 不存在，无法设置样式；请先创建该对象（或核对对象名大小写）`
          };
        }
        // 原生 API 直接调用（无原生 API 的 opacity 走 evalCommand，见下）
        if (cmd.color) {
          const [r, g, b] = hexToRgb(cmd.color);
          api.setColor(cmd.target, r, g, b);
        }
        if (cmd.thickness !== undefined) api.setLineThickness(cmd.target, cmd.thickness);
        if (cmd.visible !== undefined) api.setVisible(cmd.target, cmd.visible);
        // ★ dashed:false 也要落地（恢复实线）——与 toolExecutor.set_style 的行为对齐
        if (cmd.dashed !== undefined) api.setLineStyle(cmd.target, cmd.dashed ? 1 : 0);
        if (cmd.pointSize !== undefined) api.setPointSize(cmd.target, cmd.pointSize);
        if (cmd.pointStyle !== undefined) api.setPointStyle(cmd.target, cmd.pointStyle);
        if (cmd.opacity !== undefined) {
          // ★ SetLineOpacity 无原生 API，只能走 evalCommand——但它是 scripting 命令，
          //    成功也返回 false，且失败（如 3D 立体不支持）同样返回 false，返回值完全不可信。
          //    生效判定分环境：真实 GGB（有 getXML）以 XML 实读为准（否则 2D 圆会被无谓双重填充，历史灰盘根因）。
          //    ★ 封闭可填充对象（圆/椭圆/多边形等 conic 类）的 opacity 意图是**填充**：
          //    SetLineOpacity 只淡边线且对圆恒“生效”，内部永远空（实测视觉审查发现）——此类直接走 SetFilling。
          const targetOpacity = cmd.opacity;
          const targetType = String((api as unknown as { getObjectType?: (n: string) => string }).getObjectType?.(cmd.target) ?? "").toLowerCase();
          const fillFirst = /conic|circle|ellipse|parabola|hyperbola|polygon/.test(targetType);
          const primaryCmd = fillFirst ? `SetFilling(${cmd.target}, ${targetOpacity})` : `SetLineOpacity(${cmd.target}, ${targetOpacity})`;
          const secondaryCmd = fillFirst ? `SetLineOpacity(${cmd.target}, ${targetOpacity})` : `SetFilling(${cmd.target}, ${targetOpacity})`;
          expanded.push(primaryCmd);
          const primOk = api.evalCommand(primaryCmd);
          const canVerify = typeof (api as unknown as { getXML?: unknown }).getXML === "function";
          const primField = fillFirst ? "fillOpacity" : "lineOpacity";
          let primApplied: boolean;
          if (canVerify) {
            const po = readOpacity(api, cmd.target)[primField];
            primApplied = po !== undefined ? Math.abs(po - targetOpacity) <= 0.02 : targetOpacity >= 0.99;
          } else {
            primApplied = primOk;
          }
          if (!primApplied) {
            expanded.push(secondaryCmd);
            const secOk = api.evalCommand(secondaryCmd);
            const secField = fillFirst ? "lineOpacity" : "fillOpacity";
            let secApplied: boolean;
            if (canVerify) {
              const so = readOpacity(api, cmd.target)[secField];
              secApplied = so !== undefined && Math.abs(so - targetOpacity) <= 0.02;
            } else {
              secApplied = secOk;
            }
            if (!secApplied) {
              return {
                ok: false,
                command: cmd,
                expanded,
                error: `对象 ${cmd.target} 设置透明度失败（SetLineOpacity 与 SetFilling 均不支持，常见于 3D 立体）；该样式要求可放弃，不要循环重试`
              };
            }
          }
        }
        return { ok: true, command: cmd, expanded };
      }

      case "view": {
        // ★ 视窗宽高比校正：以画布实际宽高比适配 AI 请求范围，避免圆变椭圆/比例失真
        const fit = fitViewToAspect(api, cmd.xmin, cmd.xmax, cmd.ymin, cmd.ymax);
        api.setCoordSystem(fit.xmin, fit.xmax, fit.ymin, fit.ymax);
        if (cmd.axesUnit && api.setAxisUnits) {
          api.setAxisUnits(1, cmd.axesUnit[0], cmd.axesUnit[1], "");
        }
        return { ok: true, command: cmd, expanded };
      }

      case "caption": {
        api.setCaption(cmd.target, cmd.text);
        api.setLabelStyle(cmd.target, 3);
        return { ok: true, command: cmd, expanded };
      }

      case "delete": {
        // ★ 与 toolExecutor.delete_object 保持一致：目标不存在时报错
        //    （提示 LLM 对象名可能拼错，而非静默 no-op 让画布处于意外状态）
        if (!api.exists(cmd.target)) {
          return {
            ok: false,
            command: cmd,
            expanded: [],
            error: `对象 ${cmd.target} 不存在，无法删除；请检查对象名（可用 list_objects 确认）`
          };
        }
        api.deleteObject(cmd.target);
        return { ok: true, command: cmd, expanded };
      }

      case "reset": {
        console.warn("[AiGGB:DIAG] ★ newConstruction() — 清空画布所有对象!");
        api.newConstruction();
        resetTmpIds();
        return { ok: true, command: cmd, expanded };
      }

      // —— 物理基元 ——
      case "vector": {
        const startOk = isCoordLiteral(cmd.from) || api.exists(cmd.from);
        if (!startOk) {
          return {
            ok: false,
            command: cmd,
            expanded: [`${cmd.name} = Vector(${cmd.from}, ${cmd.to})`],
            error: `起点 ${cmd.from} 不存在；请先用 eval 命令创建该 Point，或把 from 改成坐标字面量如 "(0,0)"`
          };
        }

        // 容错：检测 "block + Gvec" 模式 —— Gvec 是 Point 则 Point+Point 会崩
        // 自动转换为 "Vector(at, at + Vector((0,0), offset))" = Point+Vector 安全形式
        const plusMatch = /^(.+)\s*\+\s*(\w[\w.]*)\s*$/.exec(cmd.to.trim());
        if (plusMatch) {
          const atExpr = plusMatch[1].trim();
          const offsetVar = plusMatch[2].trim();
          // 偏移量必须是已存在的 GGB 对象（不是字面量）才需要转换
          if (!isCoordLiteral(offsetVar) && api.exists(offsetVar)) {
            // 两步法：先把偏移量转成真正的 Vector，再 Point+Vector
            // 临时名用全局自增 id，避免多条 vector op 复用同名临时对象导致动态污染
            // ★ 临时名不能以 "_" 开头——GGB 拒绝下划线开头的对象名（实测 evalCommand 恒 false）
            const tmpId = genTmpId("tmpVv");
            const c1 = `${tmpId} = Vector((0,0), ${offsetVar})`;
            expanded.push(c1);
            let ok = api.evalCommand(c1);
            if (ok) {
              const c2 = `${cmd.name} = Vector(${atExpr}, ${atExpr} + ${tmpId})`;
              expanded.push(c2);
              ok = api.evalCommand(c2);
              api.setVisible(tmpId, false);
            }
            if (ok) {
              if (cmd.color) {
                const [r, g, b] = hexToRgb(cmd.color);
                api.setColor(cmd.name, r, g, b);
              }
              api.setLineThickness(cmd.name, 4);
              if (cmd.label) {
                api.setCaption(cmd.name, cmd.label);
                api.setLabelStyle(cmd.name, 3);
              }
              return { ok: true, command: cmd, expanded };
            }
            return {
              ok: false,
              command: cmd,
              expanded,
              error: `矢量创建失败：to="${cmd.to}" 中 ${offsetVar} 是 Point 不是 Vector（GGB 中 Point+Point 未定义）。请改用 forceDiagram op，或把偏移量写成画括字面量 "(...,...)" 而非赋给变量`
            };
          }
        }

        const vCmd = `${cmd.name} = Vector(${cmd.from}, ${cmd.to})`;
        expanded.push(vCmd);
        const ok = api.evalCommand(vCmd);
        if (!ok) {
          return {
            ok,
            command: cmd,
            expanded,
            error: `矢量创建失败。可能原因：(1) to="${cmd.to}" 包含 Point+Point；(2) to 表达式出现 NaN/除零。请改用 forceDiagram op 自动处理类型安全，或确保 to 为坐标字面量`
          };
        }
        if (cmd.color) {
          const [r, g, b] = hexToRgb(cmd.color);
          api.setColor(cmd.name, r, g, b);
        }
        api.setLineThickness(cmd.name, 4);
        if (cmd.label) {
          api.setCaption(cmd.name, cmd.label);
          api.setLabelStyle(cmd.name, 3);
        }
        return { ok: true, command: cmd, expanded };
      }

      case "forceDiagram": {
        const startOk = isCoordLiteral(cmd.at) || api.exists(cmd.at);
        if (!startOk) {
          return {
            ok: false,
            command: cmd,
            expanded: [],
            error: `力图作用点 ${cmd.at} 不存在；请先用 eval 命令创建该 Point（例如 "block = (2*cos(theta), 2*sin(theta))")，或把 at 改成坐标字面量`
          };
        }
        const failed: string[] = [];
        for (let fi = 0; fi < cmd.forces.length; fi++) {
          const f = cmd.forces[fi];
          // 临时名全局自增：单个 forceDiagram 内 fi 唯一，跨命令也唯一
          // ★ 同上：_fv 前缀会导致 GGB 静默拒绝，forceDiagram 全部力矢量失败
          const tmpId = genTmpId("tmpFv");
          // Step 1: 用 Vector((0,0), endPoint) 创建位移矢量（而非 Point 相加）
          //         GGB 中 Vector((0,0),(dx,dy)) 始终产生 Vector，不会被推断为 Point
          const tmpCmd = `${tmpId} = Vector((0,0), ${f.vec})`;
          expanded.push(tmpCmd);
          let ok = api.evalCommand(tmpCmd);
          if (!ok) {
            failed.push(`${f.name} (位移矢量创建失败)`);
            continue;
          }
          // Step 2: Point + Vector = translated Point（GGB 保证成立）
          const vCmd = `${f.name} = Vector(${cmd.at}, ${cmd.at} + ${tmpId})`;
          expanded.push(vCmd);
          ok = api.evalCommand(vCmd);
          if (!ok) {
            failed.push(`${f.name} (终点计算失败)`);
            continue;
          }
          if (f.color) {
            const [r, g, b] = hexToRgb(f.color);
            api.setColor(f.name, r, g, b);
          } else {
            const [r, g, b] = hexToRgb("#e53935");
            api.setColor(f.name, r, g, b);
          }
          api.setLineThickness(f.name, 4);
          if (f.label) {
            api.setCaption(f.name, f.label);
            api.setLabelStyle(f.name, 3);
          }
          // 隐藏辅助矢量
          api.setVisible(tmpId, false);
        }
        return failed.length === 0
          ? { ok: true, command: cmd, expanded }
          : { ok: false, command: cmd, expanded, error: `力矢量创建失败: ${failed.join(", ")}` };
      }

      case "physicsTrace": {
        // MVP 阶段：trail 走原生 trace；stroboscopic 也启用 trace，并提示 AI 在需要时显式生成 Sequence 采样
        api.setTrace(cmd.target, true);
        return { ok: true, command: cmd, expanded };
      }

      case "unitAxes": {
        const xLabel = cmd.xLabel ? `${cmd.xLabel}/${cmd.xUnit}` : `/${cmd.xUnit}`;
        const yLabel = cmd.yLabel ? `${cmd.yLabel}/${cmd.yUnit}` : `/${cmd.yUnit}`;
        api.setAxisLabels(1, xLabel, yLabel, "");
        return { ok: true, command: cmd, expanded };
      }

      case "constants": {
        for (const name of cmd.names) {
          const def = PHYSICS_CONSTANTS[name];
          if (!def) continue;
          if (api.exists(name)) continue; // 已存在不重复
          const c = `${name} = ${formatGGBNumber(def.value)}`;
          expanded.push(c);
          api.evalCommand(c);
          api.setVisible(name, false); // 常量不显示在画布
        }
        return { ok: true, command: cmd, expanded };
      }

      default: {
        // schema 已约束 op，此处兜底防未来扩展遗漏
        return {
          ok: false,
          command: cmd,
          expanded,
          error: `未知 op：${(cmd as { op?: string }).op ?? "(无)"}`
        };
      }
    }
  } catch (err) {
    console.error(`[AiGGB:DIAG] executeOne 抛出异常 (${cmd.op}):`, err instanceof Error ? err.message : String(err));
    return {
      ok: false,
      command: cmd,
      expanded,
      error: err instanceof Error ? err.message : String(err)
    };
  }
}

// ──── 画布配置 ────

/** 画布模式 */
export type CanvasMode = "2d" | "3d";
/** 领域 */
export type CanvasDomain = "general" | "physics";

/**
 * 根据 domain / mode 应用画布级配置。
 * 在 domain 切换或画布就绪时调用。
 */
export function applyCanvasConfig(
  api: GGBAppletApi,
  domain: CanvasDomain,
  mode: CanvasMode
): void {
  // 教学场景防误操作：禁止右键菜单和标签拖拽
  api.enableRightClick(false);
  api.enableLabelDrags(false);

  // 物理 / 立体几何模式下开启 3D
  if (mode === "3d") {
    api.enable3D(true);
  }

  // 关闭即时点创建（防止工具误触生成匿名点）
  api.setOnTheFlyPointCreationActive(false);

  // 网格：物理域默认开启便于量读
  if (domain === "physics") {
    api.setGridVisible(true);
  }

  // 错误对话框关闭（静默处理，由前端捕获展示）
  api.setErrorDialogsActive(false);
}

// ──── 导出 ────

/** 导出当前画布为 base64 编码的 .ggb 文件内容（不含 data: 前缀） */
export function exportGGB(api: GGBAppletApi): Promise<string> {
  return new Promise(resolve => {
    api.getBase64(data => resolve(data));
  });
}

/** 导出 PNG（DataURL） */
export function exportPNG(api: GGBAppletApi): string {
  const base64 = api.getPNGBase64(1, false, 96);
  return `data:image/png;base64,${base64}`;
}

// ──── 画布快照（供满足度评估使用） ────

/**
 * 读取对象透明度（0~1），区分"线透明度"与"填充透明度"。
 *
 * 背景：AI 设置透明度走 SetLineOpacity（线透明度），但 GGB API 的 getFilling 只返回
 * "填充透明度"——0 表示无内部填充（点/线/空心对象默认），**不代表对象不可见**。
 * 用 getFilling 当作整体透明度会把所有无填充对象标成 opacity=0（即历史 bug：审查误报"透明度为零"）。
 *
 * 正确读取：getXML(label) 的对象 XML——实测 5.4.927/5.4.929：
 * - 线透明度在 `<lineStyle ... opacity="N"/>` 属性中，**0~255 刻度**（默认 178 ≈ 0.7），
 *   SetLineOpacity(0.5) 后变为 128（=0.502）；对线段/圆（conic）均生效并写入该属性。
 *   （旧版文档所称 lineOpacity="0~100" 属性在当前版本 XML 中不存在，保留解析仅作兼容。）
 * - 填充透明度：XML fillOpacity 属性，缺失时回退 getFilling()（无填充默认 0）。
 * 两者均返回 0~1；无法读取时返回 undefined（调用方据此跳过，不产生误报）。
 */
/** GGB 新建对象的默认线透明度（XML lineStyle opacity=178 / 255） */
export const GGB_DEFAULT_LINE_OPACITY = 178 / 255;

function readOpacity(
  api: GGBAppletApi, name: string
): { lineOpacity?: number; fillOpacity?: number } {
  const anyApi = api as unknown as { getXML?: (label: string) => string };
  let lineOpacity: number | undefined;
  let fillOpacity: number | undefined;

  if (typeof anyApi.getXML === "function") {
    try {
      const xml = anyApi.getXML(name);
      const lineStyle = /<lineStyle[^>]*?opacity="([\d.]+)"/.exec(xml)?.[1];
      const line = lineStyle ?? /lineOpacity="([\d.]+)"/.exec(xml)?.[1];
      const fill = /fillOpacity="([\d.]+)"/.exec(xml)?.[1];
      // lineStyle opacity 是 0~255 刻度；遗留 lineOpacity 属性按 0~100 解析
      if (lineStyle !== undefined) lineOpacity = Math.min(1, Math.max(0, parseFloat(lineStyle) / 255));
      else if (line !== undefined) lineOpacity = Math.min(1, Math.max(0, parseFloat(line) / 100));
      if (fill !== undefined) fillOpacity = Math.min(1, Math.max(0, parseFloat(fill) / 100));
    } catch { /* getXML 不可用时走 getFilling 回退 */ }
  }

  if (fillOpacity === undefined) {
    try {
      const filling = api.getFilling?.(name);
      if (filling !== undefined) fillOpacity = Math.min(1, Math.max(0, filling));
    } catch { /* 无填充读取能力 */ }
  }

  return { lineOpacity, fillOpacity };
}

/**
 * 生成画布富文本快照：对象名 → 类型 → 定义 → 值 → 样式 → 标注 → 可见性。
 * 包含完整的样式元数据（颜色/粗细/线型/透明度/点样式），
 * 供纯文本模型做逻辑审查——不需要"看"图形，读快照即可核对。
 */
export function getRichSnapshot(api: GGBAppletApi): string {
  const names = api.getAllObjectNames();
  console.log("[AiGGB:DIAG] getRichSnapshot —", names.length, "个对象:", names.slice(0, 10).join(","), names.length > 10 ? `...(${names.length - 10} more)` : "");
  if (names.length === 0) return "(空画布)";

  const lines: string[] = [];
  for (const name of names) {
    const parts: string[] = [];
    const type = api.getObjectType(name);
    const def = api.getCommandString(name) || api.getDefinitionString(name);

    // 基本信息：名称、类型、定义
    parts.push(`${name} (${type}): ${def}`);

    // 值（点取坐标，其他取值）
    try {
      if (type === "point" || type === "vector") {
        const xs = safeNum(api.getXcoord(name));
        const ys = safeNum(api.getYcoord(name));
        const zs = safeNum(api.getZcoord?.(name));
        parts.push(`  pos=(${xs}, ${ys}${zs !== undefined ? `, ${zs}` : ""})`);
      } else if (type === "slider" || type === "number") {
        parts.push(`  value=${safeNum(api.getValue(name))}`);
      }
    } catch { /* 部分对象可能无值 */ }

    // 样式元数据（仅当非默认值时输出，减少 token）
    try {
      const styleMeta: string[] = [];
      const color = api.getColor(name);
      if (color && color !== "#000000") styleMeta.push(`color=${color}`);
      const thickness = api.getLineThickness?.(name);
      if (thickness && thickness > 1) styleMeta.push(`thickness=${thickness}`);
      const lineStyle = api.getLineStyle?.(name);
      if (lineStyle && lineStyle > 0) styleMeta.push(`dashed`);
      // ★ 透明度：区分"线透明度"（AI 用 SetLineOpacity 设置的）与"填充透明度"。
      //   无填充（=0）是点/线/空心对象的默认，绝不输出——否则模型误判对象不可见。
      const { lineOpacity, fillOpacity } = readOpacity(api, name);
      // 仅输出显式设置过的线透明度：GGB 默认 0.7（178/255）不算设置，避免快照逐对象噪音
      if (lineOpacity !== undefined && lineOpacity < 1 && Math.abs(lineOpacity - GGB_DEFAULT_LINE_OPACITY) > 0.02) {
        styleMeta.push(`lineOpacity=${lineOpacity.toFixed(2)}`);
      }
      if (fillOpacity !== undefined && fillOpacity > 0 && fillOpacity < 1) styleMeta.push(`fillOpacity=${fillOpacity.toFixed(2)}`);
      const ptSize = api.getPointSize?.(name);
      // Point size default varies; report only if set
      const ptStyle = api.getPointStyle?.(name);
      if (ptStyle !== undefined && ptStyle > 0) styleMeta.push(`pointStyle=${ptStyle}`);
      if (ptSize !== undefined && ptSize > 1) styleMeta.push(`pointSize=${ptSize}`);
      const caption = api.getCaption?.(name, true);
      if (caption) styleMeta.push(`caption="${caption}"`);
      const visible = api.getVisible(name);
      if (visible === false) styleMeta.push(`hidden`);
      if (styleMeta.length) parts.push(`  style: ${styleMeta.join(", ")}`);
    } catch { /* 样式 API 可能对新对象类型不可用 */ }

    lines.push(parts.join("\n"));
  }
  return lines.join("\n\n");
}

function safeNum(v: number | undefined | null): string {
  if (v === undefined || v === null) return "?";
  if (!Number.isFinite(v)) return v.toString();
  return Number.isInteger(v) ? v.toString() : v.toFixed(4);
}

// ──── 工具函数 ────

export function hexToRgb(hex: string): [number, number, number] {
  const m = hex.replace(/^#/, "");
  return [
    parseInt(m.slice(0, 2), 16),
    parseInt(m.slice(2, 4), 16),
    parseInt(m.slice(4, 6), 16)
  ];
}

/** 粗略判断字符串是不是 GGB 坐标字面量，如 "(1,2)" / "(a, b+1)" */
function isCoordLiteral(s: string): boolean {
  return /^\s*\(/.test(s) && /\)\s*$/.test(s);
}

/** 把一组 ExecResult 中失败项汇总成传给 prompts.buildCheckerPrompt 的入参 */
export function collectFailures(results: ExecResult[]): { cmd: string; error: string }[] {
  return results
    .filter(r => !r.ok)
    .map(r => ({
      cmd: r.expanded.length ? r.expanded.join("; ") : JSON.stringify(r.command),
      error: diagnose(r) ?? "未知错误"
    }));
}

/** 启发式诊断：根据失败命令内容给出更具体的修复建议 */
function diagnose(r: ExecResult): string | undefined {
  const raw = r.error ?? "";
  if (/Vector/i.test(raw)) {
    const cmdStr = r.expanded.join(" ") + JSON.stringify(r.command);
    if (/\/\s*Emag/i.test(cmdStr) || /\/\s*E_y/i.test(cmdStr) || /\/\s*E\b/i.test(cmdStr)) {
      return `矢量创建失败，很可能是分母在某个网格点处为零。请检查：(1) 分母是否加了 +0.001 防除零？(2) Ex/Ey 在电荷位置处是否产生 NaN？`;
    }
    if (/\b(Ex|Ey|E)\s*\(/i.test(cmdStr)) {
      const cmdStr2 = r.expanded.join(" ");
      if (!/\+0\.0*[0-9]/.test(cmdStr2)) {
        return `矢量创建失败，疑似分母缺少 epsilon 防护。请在所有分母含 (x-x0)^2+y^2 的表达式中加 +0.001：((x-x0)^2+y^2+0.001)^1.5`;
      }
    }
    return `矢量创建失败，可能原因：(1) from/to 使用了尚未声明的对象名；(2) 表达式在 Sequence 内产生了 undefined；(3) 分母除零。请用坐标字面量 "(x,y)" 代替对象名，并在所有距离平方分母中加 +0.001`;
  }
  if (/Slider/i.test(raw)) {
    return "滑块创建失败，检查 Slider(min, max, step, ...) 参数是否合法（step>0, min<max）";
  }
  return raw;
}
